/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

// ============================================================================
// CLERK-INTEGRATED SUPABASE CLIENT - NATIVE INTEGRATION
// ============================================================================

import { getSupabaseToken } from "@/lib/auth/token-utils";
import { logger } from "@/lib/utils/logger";
import type { Database } from "@/types";
import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import {
  getSupabaseConfig,
  isLocalDevelopment,
  validateClientEnvironment,
  validateEnvironment,
} from "./config";

/**
 * Creates a Supabase client that uses Clerk session tokens for authentication
 * Updated to use native Clerk-Supabase integration with JWKS validation
 */
export interface ClerkSession {
  getToken: (options?: { template?: string }) => Promise<string | null>;
  userId: string;
  organizationId?: string;
}

let singletonKey: string | null = null;
let singletonClient: ReturnType<typeof createClient<Database>> | null = null;
// Suppress noisy duplicate logs in dev/server by logging creation only once per process
let hasLoggedClerkClientCreation = false;
let hasLoggedDevClientCreation = false;

export function createClerkSupabaseClient(
  sessionOrToken:
    | { accessToken?: string; tokenTemplate?: string }
    | ClerkSession,
): ReturnType<typeof createClient<Database>> {
  const isTest = process.env.NODE_ENV === "test";
  const isBuild =
    process.env.NODE_ENV === "production" &&
    process.env["NEXT_PHASE"] === "phase-production-build";

  // Skip validation during build phase to prevent build-time failures
  if (!isTest && !isBuild) {
    const validation =
      typeof window === "undefined"
        ? validateEnvironment()
        : validateClientEnvironment();
    if (!validation.valid) {
      throw new Error(
        `Environment validation failed: ${validation.errors.join(", ")}`,
      );
    }
  }

  const config = getSupabaseConfig();

  if (isTest) {
    const looksLikeSession =
      typeof (sessionOrToken as ClerkSession)?.getToken === "function" &&
      typeof (sessionOrToken as ClerkSession)?.userId === "string";
    if (looksLikeSession) {
      const clientMaybe = getClerkSupabaseClient(
        sessionOrToken as ClerkSession,
      );
      if (clientMaybe) return clientMaybe;
      if (config.url && config.anonKey) {
        return createClient<Database>(config.url, config.anonKey, {
          auth: {
            autoRefreshToken: false,
            persistSession: false,
            detectSessionInUrl: false,
          },
        });
      }
      throw new Error(
        "Test environment requires NEXT_PUBLIC_SUPABASE_URL and NEXT_PUBLIC_SUPABASE_ANON_KEY",
      );
    }
    if (config.url && config.anonKey) {
      return createClient<Database>(config.url, config.anonKey, {
        auth: {
          autoRefreshToken: false,
          persistSession: false,
          detectSessionInUrl: false,
        },
      });
    }
  }

  const providedToken = (sessionOrToken as { accessToken?: string })
    ?.accessToken as string | undefined;
  // Use Clerk's default JWT - no template needed
  if (!providedToken && typeof window === "undefined") {
    throw new Error(
      "Access token is required for authenticated Supabase client on the server",
    );
  }

  if (typeof window !== "undefined") {
    if (window.__ECP_SUPABASE_CLERK_CLIENT__) {
      singletonClient = window.__ECP_SUPABASE_CLERK_CLIENT__ as ReturnType<
        typeof createClient<Database>
      >;
      return singletonClient;
    }
    if (singletonClient) {
      return singletonClient;
    }
  }

  const shouldLog =
    isLocalDevelopment() &&
    (typeof window === "undefined" || !window.__ECP_SUPABASE_CLERK_CLIENT__);
  if (shouldLog && !hasLoggedClerkClientCreation) {
    logger.info(
      `🔧 Creating Clerk-Supabase client for ${config.environment} environment`,
    );
    logger.info(`📍 Supabase URL: ${config.url}`);
    logger.info(`🔑 Session token exists: ${!!providedToken}`);
    logger.info(`🔑 Session token length: ${providedToken?.length ?? 0}`);
    logger.info(
      `🔑 Authorization header: ${providedToken ? "Bearer [REDACTED]" : "NULL"}`,
    );
    logger.info(`🔧 Using native Clerk integration with JWKS validation`);
    hasLoggedClerkClientCreation = true;
  }

  const storageKey =
    typeof window !== "undefined"
      ? (() => {
          if (!window.__ECP_TAB_ID__) {
            window.__ECP_TAB_ID__ = `ecp_${Math.random().toString(36).slice(2)}`;
          }
          const unique = `${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;
          return `ecp-clerk:${window.__ECP_TAB_ID__}:${unique}`;
        })()
      : `ecp-clerk:server:${Date.now().toString(36)}`;

  const client = createClient<Database>(config.url, config.anonKey, {
    auth: {
      autoRefreshToken: false,
      persistSession: false,
      detectSessionInUrl: false,
      storageKey,
    },
    // Use native Clerk-Supabase integration with accessToken function
    accessToken: async () => {
      if (providedToken) {
        return providedToken;
      }

      if (typeof window !== "undefined") {
        try {
          // Use Clerk's default JWT
          const getToken = window?.Clerk?.session?.getToken;
          if (typeof getToken === "function") {
            const token = await getToken();
            return token ?? null;
          }
          return null;
        } catch (error) {
          logger.warn("Failed to get Clerk token for accessToken function", {
            error: error instanceof Error ? error.message : String(error),
          });
          return null;
        }
      } else if (
        typeof (sessionOrToken as ClerkSession)?.getToken === "function"
      ) {
        try {
          const token = await getSupabaseToken(
            (sessionOrToken as ClerkSession).getToken,
          );
          return token;
        } catch (error) {
          logger.warn(
            "Failed to get Clerk token for accessToken session call",
            {
              error: error instanceof Error ? error.message : String(error),
            },
          );
          return null;
        }
      }

      return null;
    },
  });
  if (typeof window !== "undefined") {
    singletonClient = client;
    singletonKey = "browser";
    if (!window.__ECP_SUPABASE_CLERK_CLIENT__) {
      window.__ECP_SUPABASE_CLERK_CLIENT__ = client as unknown;
    }
  }
  return client;
}

export function createServiceRoleClient(): SupabaseClient<Database> {
  if (typeof window !== "undefined") {
    throw new Error("Service role client cannot be created on the client side");
  }
  // Allow service role client when:
  // 1. Running tests (NODE_ENV=test)
  // 2. Local development with LOCAL_DEV_SERVICE_ROLE=true
  // 3. Test bypass explicitly allowed
  // 4. SUPABASE_SERVICE_ROLE_KEY is present (server-side: webhooks, cron, etc.)
  //    The client-side guard above already prevents browser usage.
  const isTest = process.env.NODE_ENV === "test";
  const isTestBypassAllowed = process.env["ALLOW_TEST_BYPASS"] === "true";
  const hasServiceRoleKey = !!process.env["SUPABASE_SERVICE_ROLE_KEY"];

  if (!isTest) {
    const allow =
      (process.env.NODE_ENV === "development" &&
        process.env["LOCAL_DEV_SERVICE_ROLE"] === "true") ||
      isTestBypassAllowed ||
      hasServiceRoleKey;
    if (!allow) {
      throw new Error(
        "Service role client is disabled: requires SUPABASE_SERVICE_ROLE_KEY, LOCAL_DEV_SERVICE_ROLE=true, or ALLOW_TEST_BYPASS=true",
      );
    }
  }

  const validation = validateEnvironment();
  if (!validation.valid) {
    throw new Error(
      `Environment validation failed: ${validation.errors.join(", ")}`,
    );
  }

  const config = getSupabaseConfig();

  if (!config.serviceRoleKey) {
    throw new Error("Service role key is required for admin operations");
  }

  if (isLocalDevelopment()) {
    logger.info(
      `🔧 Creating service role client for ${config.environment} environment`,
    );
  }

  return createClient<Database>(config.url, config.serviceRoleKey, {
    auth: {
      autoRefreshToken: false,
      persistSession: false,
      detectSessionInUrl: false,
      storageKey: "ecp-service-role",
    },
  });
}

export function createDevelopmentClient(): SupabaseClient<Database> {
  if (typeof window !== "undefined") {
    throw new Error("Development client cannot be created on the client side");
  }
  const isTest = process.env.NODE_ENV === "test";
  if (!isTest) {
    const allow =
      process.env.NODE_ENV === "development" &&
      process.env["LOCAL_DEV_SERVICE_ROLE"] === "true";
    if (!allow) {
      throw new Error(
        "Development client is disabled outside local development with LOCAL_DEV_SERVICE_ROLE=true",
      );
    }
  }

  const validation = validateEnvironment();
  if (!validation.valid) {
    throw new Error(
      `Environment validation failed: ${validation.errors.join(", ")}`,
    );
  }

  const config = getSupabaseConfig();

  if (!config.serviceRoleKey) {
    throw new Error("Service role key is required for development client");
  }

  if (isLocalDevelopment() && !hasLoggedDevClientCreation) {
    logger.info(
      `🔧 Creating development client for ${config.environment} environment`,
    );
    hasLoggedDevClientCreation = true;
  }

  return createClient<Database>(config.url, config.serviceRoleKey, {
    auth: {
      autoRefreshToken: false,
      persistSession: false,
      detectSessionInUrl: false,
      storageKey: "ecp-dev",
    },
  });
}

export function createAppropriateClient(session?: {
  accessToken: string;
}): SupabaseClient<Database> {
  if (isLocalDevelopment()) {
    const allowServiceRole = process.env["LOCAL_DEV_SERVICE_ROLE"] === "true";
    if (allowServiceRole) {
      logger.info(
        "🔧 Local development detected - using development client (service role enabled)",
      );
      return createDevelopmentClient();
    }
    if (session?.accessToken) {
      return createClerkSupabaseClient(session);
    }
    logger.warn(
      "⚠️ Local development without service role: no session provided",
    );
  }

  if (session?.accessToken) {
    return createClerkSupabaseClient(session);
  }

  throw new Error("Authentication required in production environment");
}

export function getClerkSupabaseClient(
  session: ClerkSession,
): ReturnType<typeof createClient<Database>> | null {
  const cacheKey = `${session.userId}:${session.organizationId ?? "no-org"}`;
  if (singletonClient && singletonKey === cacheKey) return singletonClient;
  if (process.env.NODE_ENV === "test") {
    const url = process.env["NEXT_PUBLIC_SUPABASE_URL"];
    const anon = process.env["NEXT_PUBLIC_SUPABASE_ANON_KEY"];
    if (!url || !anon) {
      return null;
    }
    const client = createClient<Database>(url, anon, {
      auth: {
        autoRefreshToken: false,
        persistSession: false,
        detectSessionInUrl: false,
      },
    });
    singletonKey = cacheKey;
    singletonClient = client;
    return client;
  }
  const tokenMaybe = (
    session as { getToken?: () => string | Promise<string> }
  ).getToken?.();
  if (typeof tokenMaybe === "string") {
    const client = createClerkSupabaseClient({ accessToken: tokenMaybe });
    singletonKey = cacheKey;
    singletonClient = client;
    return client;
  }
  if (
    isLocalDevelopment() &&
    process.env["LOCAL_DEV_SERVICE_ROLE"] === "true"
  ) {
    const client = createDevelopmentClient();
    singletonKey = cacheKey;
    singletonClient = client;
    return client;
  }
  throw new Error(
    "getClerkSupabaseClient requires a synchronous token in non-test environments",
  );
}

export function clearClerkSupabaseClient(): void {
  singletonClient = null;
  singletonKey = null;
}

export function getSupabaseAdmin(): SupabaseClient<Database> | null {
  const url =
    process.env["NEXT_PUBLIC_SUPABASE_URL"] ?? process.env["SUPABASE_URL"];
  const key = process.env["SUPABASE_SERVICE_ROLE_KEY"];
  const isTest = process.env.NODE_ENV === "test";
  if ((process.env.NODE_ENV === "development" || isTest) && url && key) {
    return createClient<Database>(url, key, {
      auth: {
        autoRefreshToken: false,
        persistSession: false,
        detectSessionInUrl: false,
      },
    });
  }
  try {
    return process.env.NODE_ENV === "development" || isTest
      ? createServiceRoleClient()
      : null;
  } catch {
    return null;
  }
}

export async function validateSupabaseConnection(
  client: ReturnType<typeof createClient>,
): Promise<boolean> {
  try {
    // OPTIMIZATION: Use lightweight query with head:true for connection check
    const { error } = await client
      .from("organizations")
      .select("organization_id", { count: "exact", head: true })
      .limit(1);
    if (error) {
      throw new Error(`Database connection failed: ${error.message}`);
    }
    return true;
  } catch (error) {
    logger.error("Supabase connection validation failed:", error);
    return false;
  }
}

export function validateSessionToken(token: string): boolean {
  if (!token) {
    logger.warn("⚠️ [AUTH] No session token provided");
    return false;
  }

  try {
    const parts = token.split(".");
    if (parts.length !== 3) {
      logger.warn("⚠️ [AUTH] Invalid JWT structure - expected 3 parts");
      return false;
    }

    const header = JSON.parse(Buffer.from(parts[0] ?? "", "base64").toString());
    if (header.alg !== "RS256" && header.alg !== "ES256") {
      logger.warn(
        `⚠️ [AUTH] Unexpected JWT algorithm: ${header.alg} (expected RS256 or ES256)`,
      );
      return false;
    }

    const payload = JSON.parse(
      Buffer.from(parts[1] ?? "", "base64").toString(),
    );

    if (!payload.sub) {
      logger.warn("⚠️ [AUTH] Missing required claim: sub (user ID)");
      return false;
    }

    if (!payload.iss || typeof payload.iss !== "string") {
      logger.warn("⚠️ [AUTH] Invalid issuer claim");
      return false;
    }

    if (isLocalDevelopment()) {
      logger.info("✅ [AUTH] Session token validation passed");
      logger.info(
        `🔑 Token claims: sub=${String(payload.sub).substring(0, 8)}..., org_id=${String(payload.org_id ?? "").substring(0, 8)}..., iss=${payload.iss}`,
      );
    }

    return true;
  } catch (error) {
    logger.warn("⚠️ [AUTH] Failed to validate session token:", error);
    return false;
  }
}

export async function testJwtConfiguration(
  client: ReturnType<typeof createClient>,
): Promise<{ success: boolean; data?: unknown; error?: string }> {
  try {
    // BUGPAT-03 category-(c): JWT configuration probe; not tenant data access
    const { data, error } = await client.rpc("test_jwt_configuration");

    if (error) {
      logger.error("❌ [JWT TEST] Failed to test JWT configuration:", error);
      return { success: false, error: error.message };
    }

    logger.info("✅ [JWT TEST] JWT configuration test results:", data);
    return { success: true, data };
  } catch (error) {
    logger.error("❌ [JWT TEST] Exception during JWT test:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
}
